import React, { useRef, useState, useEffect } from 'react';
import { Volume2, VolumeX, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

const VideoPlayer = ({ videoUrl, initialMuted = false }) => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(initialMuted);
  const [hasEnded, setHasEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      setHasEnded(false);
      setIsPlaying(false);
      setIsMuted(initialMuted);
      videoRef.current.muted = initialMuted;
      videoRef.current.load();
      
      // Try to autoplay
      const tryPlay = async () => {
        try {
          await videoRef.current.play();
          setIsPlaying(true);
          setUserInteracted(true);
        } catch (error) {
          console.log('Autoplay prevented:', error);
        }
      };

      const timer = setTimeout(tryPlay, 100);
      
      return () => clearTimeout(timer);
    }
  }, [videoUrl, initialMuted]);

  const handleVideoClick = () => {
    if (videoRef.current && videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleToggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setHasEnded(false);
      setIsPlaying(true);
    }
  };

  const handleVideoEnd = () => {
    setHasEnded(true);
    setIsPlaying(false);
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  return (
    <div className="relative w-full max-w-[700px] mx-auto">
      {/* Video Container */}
      <div className="relative rounded-lg overflow-hidden shadow-2xl" onClick={handleVideoClick}>
        <video
          ref={videoRef}
          className="w-full aspect-video bg-black cursor-pointer"
          autoPlay
          muted={isMuted}
          playsInline
          onEnded={handleVideoEnd}
          onPlay={handlePlay}
          onPause={handlePause}
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Video Controls Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 flex items-center justify-between">
          <div className="flex gap-2">
            {/* Mute/Unmute Button */}
            <Button
              onClick={handleToggleMute}
              variant="ghost"
              size="icon"
              className="text-white hover:text-[#FFD700] hover:bg-white/10 transition-all duration-300"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </Button>

            {/* Replay Button */}
            {hasEnded && (
              <Button
                onClick={handleReplay}
                variant="ghost"
                size="icon"
                className="text-white hover:text-[#FFD700] hover:bg-white/10 transition-all duration-300 animate-fade-in"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;